<template>
  <v-dialog :value="value" max-width="960" scrollable @input="$emit('input', $event)">
    <v-card v-if="value && firmware">
      <v-card-title>{{ $t('firmware.trial.title', {version: firmware.name}) }}</v-card-title>
      <v-card-subtitle>{{ $t('firmware.trial.explained', {days: report ? report.trialDays : 7}) }}</v-card-subtitle>
      <v-divider />
      <v-card-text class="pt-4">
        <div v-if="loading" class="text-center my-6"><v-progress-circular indeterminate color="primary" /></div>
        <template v-else-if="report">
          <v-alert :type="PROPOSAL_TYPES[report.proposal]" text dense>
            <div class="font-weight-medium">{{ $t('firmware.trial.proposals.' + report.proposal) }}</div>
            <div v-for="reason in report.reasons" :key="reason">{{ $t('firmware.trial.reasons.' + reason) }}</div>
          </v-alert>

          <div class="text-subtitle-2 mb-1">{{ $t('firmware.trial.pilots') }}</div>
          <v-simple-table dense class="mb-4">
            <thead>
              <tr>
                <th>{{ $t('firmware.charging-point') }}</th>
                <th>{{ $t('firmware.trial.state') }}</th>
                <th>{{ $t('firmware.trial.installed') }}</th>
                <th>{{ $t('firmware.trial.metrics.disconnections') }}</th>
                <th>{{ $t('firmware.trial.metrics.offline') }}</th>
                <th>{{ $t('firmware.trial.metrics.errors') }}</th>
                <th>{{ $t('firmware.trial.metrics.sessions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!report.pilots.length"><td colspan="7" class="text--secondary">{{ $t('firmware.trial.no-pilot') }}</td></tr>
              <tr v-for="pilot in report.pilots" :key="pilot.chargingPointId">
                <td>
                  <router-link :to="{name: 'charging-points.detail.manage', params: {id: pilot.chargingPointId}}">{{ pilot.chargingPointName }}</router-link>
                  <div class="text-caption text--secondary">{{ pilot.vendor || '-' }} · {{ pilot.model || '-' }}</div>
                </td>
                <td>
                  <v-chip x-small :color="STATE_COLORS[pilot.state]" text-color="white">{{ $t('firmware.trial.states.' + pilot.state) }}</v-chip>
                </td>
                <td class="text-caption">{{ formatDate(pilot.installedAt) }}<div class="text--secondary">{{ $t('firmware.trial.until', {date: formatDate(pilot.trialEndsAt, 'DD/MM')}) }}</div></td>
                <td :class="cellClass(pilot, 'disconnections')">{{ perDay(pilot.before, 'disconnections') }} → {{ perDay(pilot.after, 'disconnections') }}</td>
                <td :class="cellClass(pilot, 'offline')">{{ perDay(pilot.before, 'offlineMinutes', 0) }} → {{ perDay(pilot.after, 'offlineMinutes', 0) }}</td>
                <td :class="cellClass(pilot, 'errors')">{{ perDay(pilot.before, 'errors') }} → {{ perDay(pilot.after, 'errors') }}</td>
                <td :class="cellClass(pilot, 'sessions')">{{ rate(pilot.before) }} → {{ rate(pilot.after) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <p class="text-caption text--secondary">{{ $t('firmware.trial.metrics-hint') }}</p>

          <template v-if="report.failedUpdates.length">
            <div class="text-subtitle-2 mb-1">{{ $t('firmware.trial.failed-updates') }}</div>
            <div v-for="(failure, index) in report.failedUpdates" :key="index" class="text-body-2 error--text">
              {{ failure.chargingPointName }} — {{ $t('firmware.engine.failures.' + failure.failureCode, {detail: failure.failureDetail || ''}) }}
            </div>
          </template>

          <template v-if="report.uncoveredModels.length">
            <div class="text-subtitle-2 mt-4 mb-1">{{ $t('firmware.trial.uncovered') }}</div>
            <v-chip v-for="model in report.uncoveredModels" :key="model.vendor + '|' + model.model" small outlined class="mr-1 mb-1">
              {{ model.productLineName || (model.vendor + ' · ' + (model.model || '-')) }} ({{ model.count }})
            </v-chip>
          </template>

          <!-- Les fiches de test faites dans cette version : au moins une verte par modèle (pour information). -->
          <div class="text-subtitle-2 mt-4 mb-1">{{ $t('firmware.trial.tests') }}</div>
          <p class="text-caption text--secondary mb-1">{{ $t('firmware.trial.tests-hint') }}</p>
          <div v-if="!(report.tests || []).length" class="text-body-2 text--secondary">{{ $t('firmware.trial.tests-none') }}</div>
          <v-simple-table v-else dense>
            <tbody>
              <tr v-for="row in report.tests" :key="row.productLineId || (row.vendor || '') + '|' + (row.model || '')">
                <td style="width: 32px"><v-icon :color="row.passed ? 'success' : 'grey lighten-1'">{{ row.passed ? 'mdi-check-circle' : 'mdi-checkbox-blank-circle-outline' }}</v-icon></td>
                <td>
                  <div class="font-weight-medium">{{ row.productLineName || ((row.vendor || '-') + ' · ' + (row.model || '-')) }}</div>
                  <div class="text-caption" :class="row.passed ? 'text--secondary' : 'orange--text'">
                    <template v-if="!row.passed">{{ $t('firmware.trial.untested') }} · </template>{{ $t('firmware.trial.tests-counts', row) }}
                  </div>
                </td>
                <td class="text-caption">
                  <div v-for="sheet in row.sheets.slice(0, 3)" :key="sheet.id">
                    <router-link :to="{name: 'charging-points.detail.manage', params: {id: sheet.chargingPointId}}">{{ sheet.chargingPointName }}</router-link>
                    — {{ $t('firmware.test.statuses.' + sheet.status) }} · {{ formatDate(sheet.closedAt || sheet.startedAt, 'DD/MM') }}
                  </div>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </template>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <template v-if="canEdit && report && firmware.status === 'testing'">
          <v-tooltip top :disabled="!validateLock">
            <template #activator="{on}">
              <span v-on="on"><v-btn small text color="success" :disabled="!!validateLock" @click="$emit('move', 'validated')">{{ $t('firmware.edit.moves.validated') }}</v-btn></span>
            </template>
            {{ validateLock }}
          </v-tooltip>
          <v-tooltip top :disabled="!blockLock">
            <template #activator="{on}">
              <span v-on="on"><v-btn small text color="error" :disabled="!!blockLock" @click="$emit('move', 'blocked')">{{ $t('firmware.edit.moves.blocked') }}</v-btn></span>
            </template>
            {{ blockLock }}
          </v-tooltip>
        </template>
        <v-spacer />
        <v-btn small text @click="load" :loading="loading">{{ $t('refresh') }}</v-btn>
        <v-btn small text @click="$emit('input', false)">{{ $t('close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import FirmwareRepository from "@repository/FirmwareRepository";

/**
 * L'épreuve d'un firmware en essai : chaque borne pilote comparée à elle-même avant et après, ce que l'épreuve propose,
 * et les boutons pour décider (valider, bloquer).
 */
export default {
  props: {
    value: {type: Boolean, default: false},
    firmware: {type: Object, default: null},
    canEdit: {type: Boolean, default: false},
    // Ce qui empêche de valider ou de bloquer (le même verrou que le menu de la fiche) ; null : rien.
    validateLock: {type: String, default: null},
    blockLock: {type: String, default: null},
  },

  data() {
    return {
      report: null,
      loading: false,
      PROPOSAL_TYPES: {validate: 'success', block: 'error', wait: 'info'},
      STATE_COLORS: {running: 'primary', ok: 'success', degraded: 'error', 'left-version': 'grey'},
    }
  },

  watch: {
    value(open) {
      if (open) {
        this.load()
      }
    },
  },

  methods: {
    load() {
      this.report = null
      this.loading = true
      FirmwareRepository.trial(this.firmware.id).then(report => {
        this.report = report
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loading = false
      })
    },

    perDay(sample, key, digits = 1) {
      return sample.days > 0 ? (sample[key] / sample.days).toFixed(digits) : '-'
    },

    rate(sample) {
      return sample.successRate === null ? '-' : Math.round(sample.successRate * 100) + ' %'
    },

    cellClass(pilot, metric) {
      return pilot.degradations.includes(metric) ? 'error--text font-weight-medium' : ''
    },
  },
}
</script>
